import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SectionTitle from "@/components/base/SectionTitle";
import WorkGrid from "@/components/base/WorkGrid";
import EmptyState from "@/components/base/EmptyState";
import { getWorkById, getRelated } from "@/utils/film";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(() => (work ? getRelated(work, 6) : []), [work]);

  useEffect(() => {
    if (work) {
      document.title = `${work.title} - 详情 - 白虎影视平台`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `${work.title}剧情介绍、主演、类型详情尽在白虎。${work.year}年${work.region}${work.genres.join(
            "、"
          )}作品，导演${work.director}。`
        );
      }
    } else {
      document.title = "影视内容不存在 - 白虎影视平台";
    }
  }, [work]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-film-line"
          title="影视内容不存在或已下架"
          description="你访问的影视作品可能已被移除或链接有误，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionHref="/"
        />
      </div>
    );
  }

  const favorite = isFavorite(work.id);
  const listHref = work.category === "movie" ? "/movies" : "/tvseries";
  const listLabel = work.category === "movie" ? "电影频道" : "电视剧频道";

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const metaRows = [
    { label: "类型", value: work.genres.join(" / ") },
    { label: "地区", value: work.region },
    { label: work.category === "movie" ? "上映年份" : "首播年份", value: `${work.year} 年` },
    { label: work.category === "movie" ? "片长" : "集数", value: work.duration ?? `${work.episodes} 集` },
    { label: "导演", value: work.director },
  ];

  return (
    <div className="animate-fadeIn">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={listHref} className="transition-colors hover:text-accent-600">
              {listLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate text-foreground-800">{work.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
            <div className="mx-auto w-full max-w-[280px] lg:mx-0">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src={work.poster}
                  alt={`${work.title} 海报`}
                  title={`${work.title} - 白虎影视平台`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {work.category === "movie" ? "电影" : "电视剧"}
                </span>
                {work.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
                {work.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <span className="font-label text-2xl font-bold text-accent-600">
                    {work.rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-foreground-500">/ 10</span>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-fire-fill text-accent-500"></i>
                  热度 {work.hot}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-calendar-line"></i>
                  {work.year}
                </span>
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-900">{row.value}</dd>
                  </div>
                ))}
                <div className="flex gap-3 text-sm sm:col-span-2">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900">{work.actors.join("、")}</dd>
                </div>
              </dl>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggle(work.id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors ${
                    favorite
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
                <Link
                  to={listHref}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{listLabel}
                </Link>
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                提示：收藏功能基于浏览器本地存储，为前端演示功能，刷新页面后仍会保留。
              </p>
            </div>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-base text-background-50">
            <i className="ri-book-open-line"></i>
          </span>
          剧情简介
        </h2>
        <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-700 md:text-base">
          {work.synopsis}
        </p>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <SectionTitle
            icon="ri-magic-line"
            title="同类推荐"
            description="根据类型与标签为你挑选的相似作品"
            moreHref={listHref}
            moreLabel={`更多${listLabel}`}
          />
          <div className="mt-8">
            <WorkGrid works={related} emptyTitle="当前暂无相关推荐" />
          </div>
        </div>
      </section>

      {toast ? (
        <div className="fixed bottom-24 left-1/2 z-[60] -translate-x-1/2 rounded-md bg-primary-900 px-4 py-2.5 text-sm text-background-50 shadow-lg md:bottom-8">
          <span className="flex items-center gap-2">
            <i className="ri-check-line text-accent-400"></i>
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}